import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { EmptyState } from '@/components/empty-state'
import { Pager } from '@/components/pager'
import { Panel } from '@/components/panel'
import { type AiRunFilters, type AiRunRow, useAiRuns } from '@/lib/admin-ai'
import { formatCount, formatExact } from '@/lib/format'
import { formatMoney, formatMoneyPrecise } from '@/lib/money'
import { cn } from '@/lib/utils'
import { Age, ClearFilters, FilterSelect, SkeletonRows, Tag, Th, Tile } from '../../-parts'

const PAGE_SIZE = 50

interface RunSearch extends AiRunFilters {
  offset?: number
}

function parseText(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export const Route = createFileRoute('/_app/admin/ai/_list/')({
  validateSearch: (search: Record<string, unknown>): RunSearch => {
    const next: RunSearch = {}
    for (const key of ['purpose', 'status', 'model', 'organizationId'] as const) {
      const value = parseText(search[key])
      if (value) next[key] = value
    }
    const offset = Number(search.offset)
    if (Number.isFinite(offset) && offset > 0) next.offset = Math.floor(offset)
    return next
  },
  component: RunsPage,
})

/**
 * Every model call this deployment has made.
 *
 * Most of them belong to nobody: background work is the deployment's own, and
 * billing it to whichever workspace happened to trigger it is a number a ledger
 * could never explain. So the workspace column is frequently empty, and that is
 * the correct reading rather than a gap.
 */
function RunsPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const offset = search.offset ?? 0

  const filters: AiRunFilters = {
    purpose: search.purpose,
    status: search.status,
    model: search.model,
    organizationId: search.organizationId,
  }

  const { data, isPending } = useAiRuns(filters, PAGE_SIZE, offset)
  const filtered = Object.values(filters).some(Boolean)

  const set = (next: Partial<RunSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...next, offset: undefined }), replace: true })

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterSelect
          all="Any purpose"
          options={data?.facets.purposes ?? []}
          value={filters.purpose}
          onChange={(next) => set({ purpose: next })}
        />
        <FilterSelect
          all="Any status"
          options={data?.facets.statuses ?? []}
          value={filters.status}
          onChange={(next) => set({ status: next })}
        />
        <FilterSelect
          all="Any model"
          options={data?.facets.models ?? []}
          value={filters.model}
          onChange={(next) => set({ model: next })}
        />
        <FilterSelect
          all="All workspaces"
          options={data?.facets.organizations ?? []}
          value={filters.organizationId}
          onChange={(next) => set({ organizationId: next })}
        />
        {filtered && (
          <ClearFilters
            onClear={() =>
              set({
                purpose: undefined,
                status: undefined,
                model: undefined,
                organizationId: undefined,
              })
            }
          />
        )}
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Tile label="Runs" value={formatExact(data?.totals.runs ?? 0)} />
          <Tile label="Tokens" value={formatCount(data?.totals.tokens ?? 0)} />
          <Tile label="Cost" value={formatMoney(data?.totals.costUsd ?? 0)} />
          <Tile
            label="Failed"
            value={formatExact(data?.totals.failed ?? 0)}
            tone={(data?.totals.failed ?? 0) > 0 ? 'bad' : undefined}
          />
        </div>

        <Panel title="Runs" flush>
          {isPending ? (
            <SkeletonRows rows={8} />
          ) : data && data.runs.length === 0 ? (
            <EmptyState
              title="No runs"
              description="Nothing has been asked of a model under those filters."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <Th align="left" className="w-[70px]">
                        Age
                      </Th>
                      <Th align="left" className="w-[150px]">
                        Purpose
                      </Th>
                      <Th align="left">Model</Th>
                      <Th align="left" className="w-[110px]">
                        Status
                      </Th>
                      <Th align="left" className="w-[160px]">
                        Attribution
                      </Th>
                      <Th className="w-[90px]">Tokens</Th>
                      <Th className="w-[90px]">Cost</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.runs.map((run) => (
                      <Row key={run.id} run={run} />
                    ))}
                  </tbody>
                </table>
              </div>

              {data && data.totals.runs > PAGE_SIZE && (
                <div className="flex justify-end border-t border-border px-4 py-2">
                  <Pager
                    offset={offset}
                    limit={PAGE_SIZE}
                    total={data.totals.runs}
                    onOffsetChange={(next) =>
                      navigate({ search: (prev) => ({ ...prev, offset: next }), replace: true })
                    }
                  />
                </div>
              )}
            </>
          )}
        </Panel>
      </div>
    </>
  )
}

function Row({ run }: { run: AiRunRow }) {
  const navigate = useNavigate()
  const now = Date.now()

  return (
    <tr
      className="cursor-pointer border-b border-border last:border-b-0 hover:bg-secondary/40"
      onClick={() => navigate({ to: '/admin/ai/runs/$runId', params: { runId: run.id } })}
    >
      <td className="py-2 pr-3 pl-3">
        <Age at={run.createdAt} now={now} />
      </td>

      <td className="px-3 py-2">
        <Link
          to="/admin/ai/runs/$runId"
          params={{ runId: run.id }}
          onClick={(event) => event.stopPropagation()}
          className="block max-w-[140px] truncate font-mono text-[12px] hover:text-link"
        >
          {run.purpose}
        </Link>
        {run.hook && (
          <span className="block max-w-[140px] truncate font-mono text-[11px] text-muted-foreground">
            {run.hook}
          </span>
        )}
      </td>

      <td className="px-3 py-2">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[12px]">{run.baseModelId}</span>
          {/* The same weights on different terms, and half the price. A tier is
              a tag beside the model, never another model. */}
          {run.variant && <Tag>{run.variant}</Tag>}
        </span>
        {run.upstreamProvider && (
          <span className="block font-mono text-[11px] text-muted-foreground">
            {run.upstreamProvider}
          </span>
        )}
      </td>

      <td className="px-3 py-2">
        <span
          className={cn('font-mono text-[12px]', run.status === 'failed' && 'text-destructive')}
        >
          {run.status}
        </span>
        {run.attempts > 1 && (
          <span className="block font-mono text-[11px] text-muted-foreground tabular">
            {run.attempts} attempts
          </span>
        )}
        {run.error && (
          <span className="block max-w-[220px] truncate font-mono text-[11px] text-destructive">
            {run.error}
          </span>
        )}
      </td>

      <td className="px-3 py-2">
        <span className="block max-w-[150px] truncate text-[12px]">{run.organization}</span>
        <span className="block max-w-[150px] truncate font-mono text-[11px] text-muted-foreground">
          {run.host}
        </span>
      </td>

      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {formatCount(run.tokens.total)}
      </td>

      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {formatMoneyPrecise(run.costUsd)}
      </td>
    </tr>
  )
}
